<template>
  <Dialog
    :visible="visible"
    @update:visible="$emit('update:visible', $event)"
    header="Request Staff Role"
    modal
    :style="{ width: '400px' }"
    data-testid="request-role-dialog"
  >
    <div class="flex flex-col gap-3 mt-1">
      <div>
        <label class="block mb-1 text-sm font-medium">Select Role <span class="text-red-500">*</span></label>
        <Select
          v-model="selectedPermissionGroupId"
          :options="roleOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Choose a role"
          class="w-full"
          :loading="loadingRoles"
          :invalid="!!roleRequest.fieldError('school_permission_group_id')"
          data-testid="role-select"
        />
        <small v-if="roleRequest.fieldError('school_permission_group_id')" class="text-red-500">
          {{ roleRequest.fieldError('school_permission_group_id') }}
        </small>
      </div>

      <div v-if="selectedPermissionGroupId && !noScopesAvailable">
        <label class="block mb-1 text-sm font-medium">Scope Type</label>
        <Select
          v-model="selectedScopeType"
          :options="scopeTypeOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Select scope type"
          class="w-full"
          showClear
          data-testid="scope-type-select"
        />
      </div>

      <div v-if="scopeTargetRequired">
        <label class="block mb-1 text-sm font-medium">Select {{ formatScopeLabel(selectedScopeType as string) }}</label>
        <Select
          v-model="selectedScopeValue"
          :options="scopeTargetOptions"
          optionLabel="label"
          optionValue="value"
          :placeholder="`Select ${formatScopeLabel(selectedScopeType as string).toLowerCase()}`"
          class="w-full"
          filter
          :loading="loadingScopeTargets"
          :emptyMessage="loadingScopeTargets ? 'Loading...' : 'No options available'"
          data-testid="scope-value-select"
        />
      </div>

      <div v-if="noScopesAvailable" class="rounded border border-yellow-200 bg-yellow-50 px-2.5 py-1.5 text-xs text-yellow-800">
        No scopes are available for this school type.
      </div>

      <div>
        <label class="block mb-1 text-sm font-medium">Starts at <span class="text-gray-400 text-xs">(optional)</span></label>
        <Calendar v-model="requestStartsAt" dateFormat="yy-mm-dd" placeholder="Select start date" class="w-full" showIcon data-testid="role-starts-at"
          :invalid="!!roleRequest.fieldError('starts_at')" />
        <small v-if="roleRequest.fieldError('starts_at')" class="text-red-500">{{ roleRequest.fieldError('starts_at') }}</small>
      </div>

      <div>
        <label class="block mb-1 text-sm font-medium">Ends at <span class="text-gray-400 text-xs">(optional)</span></label>
        <Calendar v-model="requestEndsAt" dateFormat="yy-mm-dd" placeholder="Select end date" class="w-full" showIcon
          :minDate="requestStartsAt || undefined" data-testid="role-ends-at" :invalid="!!roleRequest.fieldError('ends_at')" />
        <small v-if="roleRequest.fieldError('ends_at')" class="text-red-500">{{ roleRequest.fieldError('ends_at') }}</small>
      </div>

      <div class="flex justify-end gap-2 mt-2">
        <Button label="Cancel" size="small" severity="secondary" outlined @click="$emit('update:visible', false)" />
        <Button
          label="Submit"
          icon="pi pi-check"
          size="small"
          :loading="requestingRole"
          :disabled="!selectedPermissionGroupId || (scopeTargetRequired && !selectedScopeValue)"
          @click="requestRole"
          data-testid="btn-submit-role-request"
        />
      </div>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import { useToast } from 'primevue/usetoast';
import { PermissionScopeTypeEnum, type RequestRoleInput } from '@/types/composition-functions';
import { useRequestRoleMutation } from '@/types/vue-apollo-helpers';
import { useSchoolFeatures } from '@/composables/useSchoolFeatures';
import { useRoleOptions } from '@/composables/useRoleOptions';
import { usePermissionScopeOptions } from '@/composables/queries/usePermissionScopeOptions';
import { useValidatedForm } from '@/composables/useValidatedForm';
import { roleRequestRules } from '@/composables/roleRequestFormRules';
import { getErrorMessage } from '@/utils/error';

defineProps<{ visible: boolean }>();
const emit = defineEmits<{ 'update:visible': [value: boolean]; submitted: [] }>();

const toast = useToast();
const { isTertiary: isTertiarySchool } = useSchoolFeatures();

const selectedPermissionGroupId = ref<string | null>(null);
const selectedScopeType = ref<PermissionScopeTypeEnum | null>(null);
const selectedScopeValue = ref<string | null>(null);
const requestStartsAt = ref<Date | null>(null);
const requestEndsAt = ref<Date | null>(null);
const roleRequest = reactive(useValidatedForm(roleRequestRules, reactive({ school_permission_group_id: selectedPermissionGroupId })));

const formatScopeLabel = (s: string) =>
  s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const TERTIARY_SCOPE_TYPES: PermissionScopeTypeEnum[] = [
  PermissionScopeTypeEnum.Level,
  PermissionScopeTypeEnum.Programme,
  PermissionScopeTypeEnum.Faculty,
  PermissionScopeTypeEnum.Department,
];
const NON_TERTIARY_SCOPE_TYPES: PermissionScopeTypeEnum[] = [
  PermissionScopeTypeEnum.ClassroomSection,
];

const availableScopeTypes = computed(() =>
  isTertiarySchool.value ? TERTIARY_SCOPE_TYPES : NON_TERTIARY_SCOPE_TYPES,
);

const scopeTypeOptions = computed(() =>
  availableScopeTypes.value.map((s) => ({ label: formatScopeLabel(s), value: s })),
);

const { scopeValueOptionsFor, loadingScopeOptions } = usePermissionScopeOptions(availableScopeTypes);

const noScopesAvailable = computed(
  () => !!selectedPermissionGroupId.value && scopeTypeOptions.value.length === 0,
);

watch(selectedPermissionGroupId, () => {
  selectedScopeType.value = null;
  selectedScopeValue.value = null;
});

watch(selectedScopeType, () => {
  selectedScopeValue.value = null;
});

const scopeTargetOptions = computed(() => scopeValueOptionsFor(selectedScopeType.value));

const scopeTargetRequired = computed(() => !!selectedScopeType.value);

// Read the real loading flag rather than inferring it from an empty list —
// conflating the two is what let the permission-scoped dropdowns sit on
// "Loading..." forever instead of showing they had returned nothing.
const loadingScopeTargets = computed(() => scopeTargetRequired.value && loadingScopeOptions.value);

const { roleOptions, loadingRoles } = useRoleOptions();

const { mutate: requestStaffRoleMutate, loading: requestingRole } = useRequestRoleMutation();

const resetForm = () => {
  selectedPermissionGroupId.value = null;
  selectedScopeType.value = null;
  selectedScopeValue.value = null;
  requestStartsAt.value = null;
  requestEndsAt.value = null;
  roleRequest.reset();
};

const formatDateStr = (d: Date | null) =>
  d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : null;

const requestRole = async() => {
  if (!(await roleRequest.validate())) return;

  const input: RequestRoleInput = {
    school_permission_group_id: selectedPermissionGroupId.value!,
    scopes: selectedScopeType.value && selectedScopeValue.value
      ? [{ scope_type: selectedScopeType.value, scope_value: selectedScopeValue.value }]
      : undefined,
    starts_at: requestStartsAt.value ? formatDateStr(requestStartsAt.value) : undefined,
    ends_at: requestEndsAt.value ? formatDateStr(requestEndsAt.value) : undefined,
  };

  try {
    await requestStaffRoleMutate({ input });
    toast.add({ severity: 'success', summary: 'Success', detail: 'Role requested successfully', life: 3000 });
    emit('update:visible', false);
    emit('submitted');
    resetForm();
  } catch (err) {
    roleRequest.handleServerErrors(err);
    if (Object.keys(roleRequest.serverErrors).length === 0) {
      toast.add({ severity: 'error', summary: 'Error', detail: getErrorMessage(err, 'Failed to request role'), life: 5000 });
    }
  }
};
</script>
